import { InfoCircleFilled } from "@ant-design/icons";
import { showErrorToast, showSuccessToast } from "@src/appV2/lib/Notifications";
import { useMutation } from "@tanstack/react-query";
import { Button, Input, Popover } from "antd";
import { formatRelative, parseISO } from "date-fns";
import { useState } from "react";
import { Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import { FacilityCategoryNote, updateNotes } from "src/api/facility";
import { PERMISSIONS } from "src/constants/facility";
import { Form, useSchemaForm } from "src/designsystem/Forms/Form";
import { VerticalSpacing } from "src/designsystem/VerticalSpacing/VerticalSpacing";
import { SessionType } from "src/modules/interface";
import { getErrorMessage } from "src/utils/errors";
import { z } from "zod";
import { facilityNoteSchema } from "./facilityNote.schema";

const { TextArea } = Input;

interface FacilityNoteProps {
  facilityId: string;
  facilityNote: FacilityCategoryNote;
  hideContent: boolean;
  onUpdateFacilityNote: (facilityNote: FacilityCategoryNote) => void;
}

export function FacilityNote({
  facilityId,
  facilityNote,
  hideContent,
  onUpdateFacilityNote,
}: FacilityNoteProps) {
  const [mode, setMode] = useState<"view" | "edit">("view");
  const { roles } = useSelector((state: SessionType) => state.session.adminProfile || []);

  const userForm = useSchemaForm({
    schema: facilityNoteSchema,
    defaultValues: {
      content: facilityNote.note,
    },
  });
  const { formState, control } = userForm;

  const updateFacilityNote = useMutation({
    mutationFn: ({
      workplaceId,
      data,
    }: {
      workplaceId: string;
      data: Partial<FacilityCategoryNote>;
    }) => updateNotes(workplaceId, data),
  });

  const saveFacilityNote = async (data: z.infer<typeof facilityNoteSchema>) => {
    try {
      const resp = await updateFacilityNote.mutateAsync({
        workplaceId: facilityId,
        data: {
          noteId: facilityNote.noteId,
          categoryId: facilityNote.categoryId,
          note: data.content,
        },
      });
      onUpdateFacilityNote({ ...facilityNote, ...resp });
      setMode("view");
      showSuccessToast("Note updated successfully");
    } catch (error) {
      showErrorToast(getErrorMessage(error));
    }
  };

  /**
   * @deprecated
   * This dangerously injects HTML in ways that can allow for XSS and other dangerous exploits.
   * Do not use this.
   * Use markdown instead.
   */
  const stringToHTML = (description: string) => (
    <div dangerouslySetInnerHTML={{ __html: description }}></div>
  );

  return (
    <>
      <div className="cat-time-email">
        <div className="categoryname-time-update">
          <h4>
            {facilityNote.category}
            <span>
              <Popover
                placement="bottom"
                overlayStyle={{ maxWidth: "50%", overflowWrap: "break-word" }}
                content={stringToHTML(facilityNote.description)}
              >
                {<InfoCircleFilled />}
              </Popover>
            </span>
          </h4>
          {!hideContent && (
            <label>
              <b>
                {facilityNote.updatedAt
                  ? formatRelative(parseISO(facilityNote.updatedAt), new Date())
                  : "--"}
              </b>
            </label>
          )}
        </div>
        {!hideContent && (
          <label>
            <i>
              <span>Added by</span>
            </i>
            <b>{facilityNote.updatedBy || "--"}</b>
          </label>
        )}
      </div>
      {!hideContent && (
        <>
          {/* eslint-disable-next-line no-nested-ternary */}
          {roles?.includes(PERMISSIONS.NOTES.NOTE_EDIT) ? (
            mode === "edit" ? (
              <>
                <Form form={userForm} onSubmit={saveFacilityNote}>
                  <Controller
                    control={control}
                    name="content"
                    render={({ field }) => (
                      <TextArea placeholder={`Enter ${facilityNote.category}`} {...field} />
                    )}
                  />
                  <div className="ant-form-item-explain-error">
                    {formState.errors.content?.message}
                  </div>
                  <VerticalSpacing size="lg" />
                  <Button
                    type="primary"
                    htmlType="submit"
                    disabled={formState.isSubmitting || !formState.isDirty}
                  >
                    Save
                  </Button>
                  <Button onClick={() => setMode("view")} style={{ marginLeft: "16px" }}>
                    Cancel
                  </Button>
                </Form>
                <VerticalSpacing />
              </>
            ) : (
              <>
                {facilityNote.note ? (
                  <p className="static-note">{facilityNote.note}</p>
                ) : (
                  <p>Click on edit to add notes</p>
                )}
                <VerticalSpacing size="md" />
                <Button className={"border-less-btn"} onClick={() => setMode("edit")}>
                  Edit
                </Button>
              </>
            )
          ) : (
            <>{facilityNote.note ? <p className="static-note">{facilityNote.note}</p> : null}</>
          )}
        </>
      )}
      <VerticalSpacing size="lg" />
    </>
  );
}
